import classNames from "classnames";
import React, { useRef, useState } from "react";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";

export interface HelpdeskDropzoneProps {
  onFiles: (files: File[]) => void;
  hint: string;
  disabled?: boolean;
  /** data-testid of the hidden file input (E2E sets files on it directly). */
  inputTestId: string;
  compact?: boolean;
}

/** Drag-and-drop or click-to-pick area for any file type; the size check is the caller's. */
const HelpdeskDropzone = ({
  onFiles,
  hint,
  disabled = false,
  inputTestId,
  compact = false
}: HelpdeskDropzoneProps): React.ReactElement => {
  const inputRef: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const emit = (list: FileList | null): void => {
    const files: File[] = list ? Array.from(list) : [];
    if (files.length > 0) onFiles(files);
  };

  const openPicker = (): void => {
    if (!disabled) inputRef.current?.click();
  };

  return (
    <div
      className={classNames("kuika-helpdesk-dropzone", {
        "kuika-helpdesk-dropzone--active": isDragging,
        "kuika-helpdesk-dropzone--disabled": disabled,
        "kuika-helpdesk-dropzone--compact": compact
      })}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={openPicker}
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>): void => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openPicker();
        }
      }}
      onDragEnter={(e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragOver={(e: React.DragEvent<HTMLDivElement>): void => e.preventDefault()}
      onDragLeave={(): void => setIsDragging(false)}
      onDrop={(e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setIsDragging(false);
        if (!disabled) emit(e.dataTransfer.files);
      }}
    >
      <KuikaSvg name="upload" width={compact ? 16 : 24} height={compact ? 16 : 24} color="var(--dark-6)" />
      <span className="kuika-helpdesk-dropzone__hint">{hint}</span>
      <input
        ref={inputRef}
        className="kuika-helpdesk-dropzone__input"
        data-testid={inputTestId}
        type="file"
        multiple
        disabled={disabled}
        onClick={(e: React.MouseEvent<HTMLInputElement>): void => e.stopPropagation()}
        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
          emit(e.target.files);
          // Allow picking the same file again after removing it.
          e.target.value = "";
        }}
      />
    </div>
  );
};

export default HelpdeskDropzone;
